import { useCallback, useContext, useEffect, useRef, useState } from 'preact/hooks'
import { SearchspringBeacon } from 'libs/searchspring/classes/searchspring-beacon'
import { createContext } from 'preact'
import { useSearchspringContext } from 'libs/searchspring/fetchers/provider'
import { useIntersectionObserver } from '@/components/hooks/useIntersectionObserver'

const defaultValue = {
  analytics: undefined,
  logPayload: () => {}
}

const BeaconAnalyticsContext = createContext(defaultValue)

export function useBeaconAnalytics() {
  const context = useContext(BeaconAnalyticsContext)
  return context ?? defaultValue
}

export function BeaconAnalyticsProvider({ profile, children }) {
  const { current: analytics } = useRef(new SearchspringBeacon(profile))

  const [queue, setQueue] = useState([])

  useEffect(() => {
    const timeout = setTimeout(() => {
      setQueue((prev) => {
        if (prev.length === 0) return prev
        analytics.sendPayload(prev)
        return []
      })
    }, 400)

    return () => {
      clearTimeout(timeout)
    }
  }, [queue])

  const logPayload = useCallback((payload) => {
    setQueue((prev) => {
      const next = [...prev, ...payload]
      return next
    })
  }, [])

  const value = { logPayload, analytics }

  return <BeaconAnalyticsContext.Provider value={value}>{children}</BeaconAnalyticsContext.Provider>
}

/**
 * Sends the formatted data to send a 'render' event to the Beacon API
 * https://docs.searchspring.com/reference/personalized-recommendations#render
 */

export function useBeaconLogRender() {
  const { analytics, logPayload } = useBeaconAnalytics()
  const { results } = useSearchspringContext()

  useEffect(() => {
    if (!analytics) return
    const payload = analytics.generateRenderPayload(results)
    logPayload(payload)
  }, [])
}

/**
 * Sends the formatted data to send an 'impression' event to the Beacon API
 * https://docs.searchspring.com/reference/personalized-recommendations#impression
 */
export function useBeaconLogImpressions(ref) {
  const [isInView, setIsInView] = useState(false)
  const { analytics, logPayload } = useBeaconAnalytics()
  const { results } = useSearchspringContext()
  const { setNode, entry } = useIntersectionObserver({})
  useEffect(() => {
    if (!ref) return
    setNode(ref)
  }, [ref])
  useEffect(() => {
    setIsInView(entry?.isIntersecting)
  }, [entry])
  useEffect(() => {
    if (!isInView || !analytics) return
    const payload = analytics.generateImpressionPayload(results)
    logPayload(payload)
  }, [isInView])
}

/**
 * Sends the formatted data to send an 'click' event to the Beacon API
 * https://docs.searchspring.com/reference/personalized-recommendations#clickimpression
 */

export function useBeaconLogClick() {
  const { analytics, logPayload } = useBeaconAnalytics()

  const handleLogClick = useCallback((result) => {
    if (!analytics) return
    const payload = analytics.generateClickPayload(result)
    logPayload(payload)
  }, [])

  return handleLogClick
}
